import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { cities } from '../utils/mockData/cities';
import { stores as mockStores, pincodes as mockPincodes, getStoresByCity, getPincodesByCity, getPincodesByStores } from '../utils/mockData/stores';
import { fetchAndMapOrders, fetchAndMapServiceOrders } from '../utils/apiMapper';

// Filter levels
export const FILTER_LEVELS = {
    GLOBAL: 'GLOBAL',
    CITY: 'CITY',
    STORE: 'STORE',
    PINCODE: 'PINCODE',
};

// LocalStorage key
const FILTER_STORAGE_KEY = 'fmcg_pro_filters';
const FILTER_EXPIRY_HOURS = 24;

// Helper to save filters to localStorage
const saveFiltersToStorage = (filters) => {
    try {
        const data = {
            ...filters,
            dateRange: {
                startDate: filters.dateRange.startDate?.toISOString(),
                endDate: filters.dateRange.endDate?.toISOString(),
            },
            timestamp: Date.now(),
        };
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
        console.warn('Failed to save filters to localStorage:', e);
    }
};

// Helper to load filters from localStorage
const loadFiltersFromStorage = () => {
    try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (!stored) return null;

        const data = JSON.parse(stored);

        // Check expiration (24 hours)
        const expiryTime = FILTER_EXPIRY_HOURS * 60 * 60 * 1000;
        if (Date.now() - data.timestamp > expiryTime) {
            localStorage.removeItem(FILTER_STORAGE_KEY);
            return null;
        }

        return {
            ...data,
            dateRange: {
                startDate: data.dateRange.startDate ? new Date(data.dateRange.startDate) : subDays(new Date(), 7),
                endDate: data.dateRange.endDate ? new Date(data.dateRange.endDate) : new Date(),
            },
        };
    } catch (e) {
        console.warn('Failed to load filters from localStorage:', e);
        return null;
    }
};

const FilterContext = createContext(null);

export function FilterProvider({ children }) {
    // Load saved filters on mount
    const savedFilters = useMemo(() => loadFiltersFromStorage(), []);

    // Date range state
    const [dateRange, setDateRange] = useState(savedFilters?.dateRange || {
        startDate: subDays(new Date(), 7),
        endDate: new Date(),
    });

    // Filter level state
    const [level, setLevel] = useState(savedFilters?.level || FILTER_LEVELS.GLOBAL);

    // Selected filter values
    const [selectedCities, setSelectedCities] = useState(savedFilters?.cities || []);
    const [selectedStores, setSelectedStores] = useState(savedFilters?.stores || []);
    const [selectedPincodes, setSelectedPincodes] = useState(savedFilters?.pincodes || []);

    // Loading state for apply
    const [isApplying, setIsApplying] = useState(false);

    // Applied filters (what's actually active)
    const [appliedFilters, setAppliedFilters] = useState(savedFilters || {
        level: FILTER_LEVELS.GLOBAL,
        cities: [],
        stores: [],
        pincodes: [],
        dateRange: {
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
        },
    });

    const [fetchedOrders, setFetchedOrders] = useState([]);
    const [fetchedServiceOrders, setFetchedServiceOrders] = useState([]);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);

    // Fetch orders when applied date range changes
    useEffect(() => {
        async function loadOrders() {
            setIsLoadingOrders(true);
            try {
                const start = appliedFilters.dateRange.startDate;
                const end = appliedFilters.dateRange.endDate;
                const [retailData, serviceData] = await Promise.all([
                    fetchAndMapOrders(start, end),
                    fetchAndMapServiceOrders(start, end),
                ]);
                setFetchedOrders(retailData);
                setFetchedServiceOrders(serviceData);
            } catch (err) {
                console.error("Failed to fetch orders for proteinDashboard:", err);
            } finally {
                setIsLoadingOrders(false);
            }
        }
        loadOrders();
    }, [appliedFilters.dateRange.startDate, appliedFilters.dateRange.endDate]);

    // Save to localStorage whenever applied filters change
    useEffect(() => {
        saveFiltersToStorage(appliedFilters);
    }, [appliedFilters]);

    // Dynamic getters from fetched orders
    const getCityNameStr = useCallback((cityId) => {
        const orderWithCity = fetchedOrders.find(o => o.cityId === cityId);
        return orderWithCity ? orderWithCity.cityName : (cities.find(c => c.id === cityId)?.name || cityId);
    }, [fetchedOrders]);

    const getStoreNameStr = useCallback((storeId) => {
        const orderWithStore = fetchedOrders.find(o => o.storeId === storeId);
        return orderWithStore ? orderWithStore.storeName : (mockStores.find(s => s.id === storeId)?.name || storeId);
    }, [fetchedOrders]);

    // Compute dynamic lists directly from the fetched dataset
    const availableCities = useMemo(() => {
        if (!fetchedOrders || fetchedOrders.length === 0) return [];

        const uniqueCityIds = [...new Set(fetchedOrders.map(o => o.cityId).filter(Boolean))];
        return uniqueCityIds.map(id => {
            const cityName = getCityNameStr(id);
            // Default to central India if coords not in mock
            const defaultMock = cities.find(c => c.id === id) || { lat: 21.1458, lng: 79.0882, state: 'Unknown' };
            return {
                id,
                name: cityName,
                state: defaultMock.state,
                lat: defaultMock.lat,
                lng: defaultMock.lng
            };
        });
    }, [fetchedOrders, getCityNameStr]);

    const availableStores = useMemo(() => {
        let relevantOrders = fetchedOrders;
        if (selectedCities.length > 0) {
            relevantOrders = relevantOrders.filter(o => selectedCities.includes(o.cityId));
        }

        const uniqueStoreIds = [...new Set(relevantOrders.map(o => o.storeId).filter(Boolean))];
        if (uniqueStoreIds.length === 0 && selectedCities.length === 0) return mockStores;

        return uniqueStoreIds.map(id => {
            return {
                id,
                name: getStoreNameStr(id),
                type: 'Store',
                cityId: relevantOrders.find(o => o.storeId === id)?.cityId || 'UNKNOWN'
            };
        });
    }, [fetchedOrders, selectedCities, getStoreNameStr]);

    // Get available pincodes based on selected cities and stores
    const availablePincodes = useMemo(() => {
        let relevantOrders = fetchedOrders;
        if (selectedStores.length > 0) {
            relevantOrders = relevantOrders.filter(o => selectedStores.includes(o.storeId));
        } else if (selectedCities.length > 0) {
            relevantOrders = relevantOrders.filter(o => selectedCities.includes(o.cityId));
        }

        const uniquePincodes = [...new Set(relevantOrders.map(o => o.pincode).filter(Boolean))];
        if (uniquePincodes.length === 0 && selectedCities.length === 0 && selectedStores.length === 0) return mockPincodes;

        return uniquePincodes.map(code => ({ code, area: 'Serving Area' }));
    }, [fetchedOrders, selectedCities, selectedStores]);

    // Update cities and cascade clear lower levels
    const setCities = useCallback((cityIds) => {
        setSelectedCities(cityIds);

        // Clear stores that are not in the selected cities
        if (cityIds.length > 0) {
            // Re-calc valid stores for these new cities
            const validOrdersForCities = fetchedOrders.filter(o => cityIds.includes(o.cityId));
            const validStoreIds = [...new Set(validOrdersForCities.map(o => o.storeId).filter(Boolean))];
            setSelectedStores(prev => prev.filter(id => validStoreIds.includes(id)));

            // Clear pincodes accordingly
            const validPincodeCodes = [...new Set(validOrdersForCities.map(o => o.pincode).filter(Boolean))];
            setSelectedPincodes(prev => prev.filter(code => validPincodeCodes.includes(code)));
        }

        // Update level
        if (cityIds.length > 0) {
            setLevel(FILTER_LEVELS.CITY);
        } else {
            setLevel(FILTER_LEVELS.GLOBAL);
            setSelectedStores([]);
            setSelectedPincodes([]);
        }
    }, []);

    // Update stores and cascade
    const setStores = useCallback((storeIds) => {
        setSelectedStores(storeIds);

        // Clear pincodes that are not in the selected stores
        if (storeIds.length > 0) {
            const validPincodes = getPincodesByStores(storeIds);
            const validPincodeCodes = validPincodes.map(p => p.code);
            setSelectedPincodes(prev => prev.filter(code => validPincodeCodes.includes(code)));
            setLevel(FILTER_LEVELS.STORE);
        } else if (selectedCities.length > 0) {
            setLevel(FILTER_LEVELS.CITY);
        }
    }, [selectedCities]);

    // Update pincodes
    const setPincodes = useCallback((pincodeCodes) => {
        setSelectedPincodes(pincodeCodes);
        if (pincodeCodes.length > 0) {
            setLevel(FILTER_LEVELS.PINCODE);
        } else if (selectedStores.length > 0) {
            setLevel(FILTER_LEVELS.STORE);
        } else if (selectedCities.length > 0) {
            setLevel(FILTER_LEVELS.CITY);
        }
    }, [selectedCities, selectedStores]);

    // Reset all filters (both selected and applied)
    const resetFilters = useCallback(() => {
        setLevel(FILTER_LEVELS.GLOBAL);
        setSelectedCities([]);
        setSelectedStores([]);
        setSelectedPincodes([]);
        const defaultDateRange = {
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
        };
        setDateRange(defaultDateRange);
        setAppliedFilters({
            level: FILTER_LEVELS.GLOBAL,
            cities: [],
            stores: [],
            pincodes: [],
            dateRange: defaultDateRange,
        });
        // Clear localStorage
        localStorage.removeItem(FILTER_STORAGE_KEY);
    }, []);

    // Clear date range filter specifically
    const clearDateFilter = useCallback(() => {
        const defaultDateRange = {
            startDate: subDays(new Date(), 7),
            endDate: new Date(),
        };
        setDateRange(defaultDateRange);
        setAppliedFilters(prev => ({
            ...prev,
            dateRange: defaultDateRange,
        }));
    }, []);

    // Apply filters with simulated loading
    const applyFilters = useCallback(async () => {
        setIsApplying(true);

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 300));

        const newApplied = {
            level,
            cities: [...selectedCities],
            stores: [...selectedStores],
            pincodes: [...selectedPincodes],
            dateRange: { ...dateRange },
        };

        setAppliedFilters(newApplied);
        setIsApplying(false);
    }, [level, selectedCities, selectedStores, selectedPincodes, dateRange]);

    // Remove a specific filter chip (also updates applied filters)
    const removeFilterChip = useCallback((type, value) => {
        switch (type) {
            case 'city':
                // When removing a city, also remove dependent stores and pincodes
                const remainingCities = selectedCities.filter(id => id !== value);
                const validStores = remainingCities.length > 0 ? getStoresByCity(remainingCities) : [];
                const validStoreIds = validStores.map(s => s.id);
                const remainingStores = selectedStores.filter(id => validStoreIds.includes(id));
                const validPincodes = remainingCities.length > 0 ? getPincodesByCity(remainingCities) : [];
                const validPincodeCodes = validPincodes.map(p => p.code);
                const remainingPincodes = selectedPincodes.filter(code => validPincodeCodes.includes(code));

                setSelectedCities(remainingCities);
                setSelectedStores(remainingStores);
                setSelectedPincodes(remainingPincodes);
                setAppliedFilters(prev => ({
                    ...prev,
                    cities: prev.cities.filter(id => id !== value),
                    stores: prev.stores.filter(id => validStoreIds.includes(id)),
                    pincodes: prev.pincodes.filter(code => validPincodeCodes.includes(code)),
                }));

                if (remainingCities.length === 0) {
                    setLevel(FILTER_LEVELS.GLOBAL);
                }
                break;
            case 'store':
                const newStores = selectedStores.filter(id => id !== value);
                setSelectedStores(newStores);
                setAppliedFilters(prev => ({
                    ...prev,
                    stores: prev.stores.filter(id => id !== value),
                }));
                break;
            case 'pincode':
                const newPincodes = selectedPincodes.filter(code => code !== value);
                setSelectedPincodes(newPincodes);
                setAppliedFilters(prev => ({
                    ...prev,
                    pincodes: prev.pincodes.filter(code => code !== value),
                }));
                break;
            default:
                break;
        }
    }, [selectedCities, selectedStores, selectedPincodes]);

    // Active filter count
    const activeFilterCount = useMemo(() => {
        return selectedCities.length + selectedStores.length + selectedPincodes.length;
    }, [selectedCities, selectedStores, selectedPincodes]);

    // Applied filter count
    const appliedFilterCount = useMemo(() => {
        return appliedFilters.cities.length + appliedFilters.stores.length + appliedFilters.pincodes.length;
    }, [appliedFilters]);

    // Has pending changes
    const hasPendingChanges = useMemo(() => {
        const dateChanged =
            dateRange.startDate?.getTime() !== appliedFilters.dateRange?.startDate?.getTime() ||
            dateRange.endDate?.getTime() !== appliedFilters.dateRange?.endDate?.getTime();

        return (
            dateChanged ||
            JSON.stringify(selectedCities) !== JSON.stringify(appliedFilters.cities) ||
            JSON.stringify(selectedStores) !== JSON.stringify(appliedFilters.stores) ||
            JSON.stringify(selectedPincodes) !== JSON.stringify(appliedFilters.pincodes)
        );
    }, [selectedCities, selectedStores, selectedPincodes, dateRange, appliedFilters]);

    // Check if date range is different from default
    const hasCustomDateRange = useMemo(() => {
        const defaultStart = subDays(new Date(), 7);
        const isDefaultRange =
            Math.abs(appliedFilters.dateRange?.startDate?.getTime() - defaultStart.getTime()) < 86400000 && // within 1 day
            Math.abs(appliedFilters.dateRange?.endDate?.getTime() - Date.now()) < 86400000;
        return !isDefaultRange;
    }, [appliedFilters.dateRange]);

    // Scope breadcrumb
    const scopeBreadcrumb = useMemo(() => {
        const parts = ['Global'];

        if (appliedFilters.cities.length > 0) {
            const cityNames = appliedFilters.cities
                .map(id => getCityNameStr(id))
                .filter(Boolean);
            parts.push(cityNames.length === 1 ? cityNames[0] : `${cityNames.length} Cities`);
        }

        if (appliedFilters.stores.length > 0) {
            const storeNames = appliedFilters.stores
                .map(id => getStoreNameStr(id))
                .filter(Boolean);
            parts.push(storeNames.length === 1 ? storeNames[0] : `${storeNames.length} Stores`);
        }

        if (appliedFilters.pincodes.length > 0) {
            parts.push(
                appliedFilters.pincodes.length === 1
                    ? appliedFilters.pincodes[0]
                    : `${appliedFilters.pincodes.length} Pincodes`
            );
        }

        return parts;
    }, [appliedFilters]);

    const value = {
        // Date range
        dateRange,
        setDateRange,
        clearDateFilter,
        hasCustomDateRange,

        // Filter level
        level,
        setLevel,

        // Selected values (for UI)
        selectedCities,
        selectedStores,
        selectedPincodes,

        // Setters with cascade logic
        setCities,
        setStores,
        setPincodes,

        // Available options (filtered by hierarchy)
        availableCities,
        availableStores,
        availablePincodes,

        // Actions
        resetFilters,
        applyFilters,
        removeFilterChip,

        // State
        isApplying,
        activeFilterCount,
        appliedFilterCount,
        hasPendingChanges,

        // Scope breadcrumb
        appliedFilters,
        scopeBreadcrumb,

        // Safe Name Formatters for Child Components
        getCityName: getCityNameStr,
        getStoreName: getStoreNameStr,

        // Data
        orders: fetchedOrders,
        serviceOrders: fetchedServiceOrders,
        isLoadingOrders,
    };

    return (
        <FilterContext.Provider value={value}>
            {children}
        </FilterContext.Provider>
    );
}

export function useFilters() {
    const context = useContext(FilterContext);
    if (!context) {
        throw new Error('useFilters must be used within a FilterProvider');
    }
    return context;
}

export default FilterContext;
